const panDetectionDefinitions = [
  {
    issuer: 'amex',
    length: [15],
    prefix: '34|37',
  },
  {
    issuer: 'dinerClub',
    length: [14],
    prefix: '30[0-5]|36|38',
  },
  {
    issuer: 'discover',
    length: [16],
    prefix:
      '60110|60112|60113|60114|601174|601177|601178|601179|601186|601187|601188|601189|60119|64[4-9]|65[4-9]',
  },
  {
    issuer: 'jcb',
    length: [15, 16],
    prefix: '3528|3529|35[3-8]',
  },
  {
    issuer: 'maestro',
    length: [12, 13, 14, 15, 16, 17, 18, 19],
    prefix: '5018|5020|5038|5044|5081|6220|6304|6759|676[1-3]|6799|504645|504817|504834|603845',
  },
  {
    issuer: 'mastercard',
    length: [16],
    prefix: '222[1-9]|22[3-9]|2[3-6]|271|2720|5[1-5]',
  },
  {
    issuer: 'visa',
    length: [16],
    prefix: '4',
  },
];

/**
 * Create regexes by combining the prefixes for each issuer for a given length
 * Return a collection of regexes sorted by length in descending order
 * Example:
 * [
 *   { length: 16, regex: /^(4)|(3528|3529)/ },
 *   { length: 15, regex: /^(34|37)/ },
 * ]
 */
const combinePanDetectionRegex = (
  panDetectionDefinition: { length: number[]; prefix: string }[]
) => {
  const perLength = panDetectionDefinition.reduce((acc: Record<number, string[]>, definition) => {
    definition.length.forEach((length) => {
      if (!acc[length]) {
        acc[length] = [];
      }
      acc[length].push(definition.prefix);
    });
    return acc;
  }, {});

  const regexes = Object.entries(perLength).map(([length, prefixes]) => {
    const prefixGroups = prefixes.map((prefixGroup) => `(${prefixGroup})`).join('|');
    return { length: parseInt(length, 10), regex: RegExp(`^(${prefixGroups})`) };
  });

  regexes.sort((a, b) => b.length - a.length);
  return regexes;
};

/**
 * Cache the ordered regexes for pan detection
 */
const orderedPanDetectionRegex = combinePanDetectionRegex(panDetectionDefinitions);

/**
 * Each line is processed separately
 * Decompose the line into multiple sequences of digits separated by spaces or dashes
 * For each sequence, generate all possible concatenation combinations based on space and dash separators
 * Run the PAN detection regexes on each combination and look for an exact match over the whole sequence
 * If a match is found, run the Luhn check
 * If the Luhn check passes, we found a PAN and exit early with TRUE
 */
const hasCreditCardInfoV3 = (value: string): boolean => {
  const minLength = orderedPanDetectionRegex[orderedPanDetectionRegex.length - 1].length;

  const sequences = extractSequenceCombinations(value).filter(
    (sequence) => sequence.length >= minLength
  );
  if (sequences.length === 0) {
    return false;
  }
  sequences.sort((a, b) => b.length - a.length);

  for (let j = 0; j < sequences.length; j++) {
    const sequence = sequences[j];

    for (let i = 0; i < orderedPanDetectionRegex.length; i++) {
      const regex = orderedPanDetectionRegex[i];
      if (regex.length !== sequence.length) {
        continue;
      }
      if (regex.regex.test(sequence)) {
        if (luhnVerification(sequence)) {
          return true;
        }
      }
    }
  }
  return false;
};

const extractSequenceCombinations = (value: string): string[] => {
  /**
   * normalize multiple spaces to a single space
   * normalize single dashes to a dash without spaces
   * word boundary - digit sequence that can be seperated by a dash or space
   */
  value = value.replace(/ +/g, ' ');
  value = value.replace(/ ?- ?/g, '-');
  const matches = value.match(/\b\d+([- ]\d+)*\b/g) || [];
  return matches.map((match) => generateCombinations(match)).flat();
};

/**
 * Generate all possible concatenation combinations of a given string
 * Example: 12 34 56 => [12, 1234, 123456, 34, 3456, 56]
 */
const generateCombinations = (match: string): string[] => {
  const parts = match.split(/[- ]/).filter((part) => part.length > 0);
  const combinations = new Set<string>();
  const length = parts.length;
  for (let i = 0; i < length; i++) {
    let currentCombination = '';
    for (let j = i; j < length; j++) {
      currentCombination += parts[j];
      combinations.add(currentCombination);
    }
  }
  return Array.from(combinations);
};

/**
 * Used the regex from
 * https://www.w3schools.blog/credit-card-validation-javascript-js
 */

const CC_ISSUER_PATTERNS = [
  {
    issuer: 'amex',
    regex: RegExp(/(3[47][0-9]{13})/),
    luhnCheck: true,
  },
  {
    issuer: 'dinerClub',
    regex: RegExp(/3(?:0[0-5]|[68][0-9])[0-9]{11}/),
    luhnCheck: true,
  },
  {
    issuer: 'discover',
    regex: RegExp(
      /65[4-9][0-9]{13}|64[4-9][0-9]{13}|6011[0-9]{12}|(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5])[0-9]{10})/
    ),
    luhnCheck: true,
  },
  {
    issuer: 'jcb',
    regex: RegExp(/(?:2131|1800|35[0-9]{3})[0-9]{11}/),
    luhnCheck: true,
  },
  {
    issuer: 'mastercard',
    regex: RegExp(
      /(5[1-5][0-9]{14})|(2(?:2(?:2[1-9]|[3-9][0-9])|[3-6][0-9][0-9]|7(?:[01][0-9]|20))[0-9]{12})/
    ),
    luhnCheck: true,
  },
  {
    issuer: 'maestro',
    regex: RegExp(
      /(5018|5081|5044|5020|5038|603845|6304|6759|676[1-3]|6799|6220|504834|504817|504645)[0-9]{8,15}/
    ),
    luhnCheck: true,
  },
  {
    issuer: 'visa',
    regex: RegExp(/(4[0-9]{12}(?:[0-9]{3})?)/),
    luhnCheck: true,
  },
];

export const SPECIAL_CHARACTERS = /([^\w]+|\s+)/g;

export const luhnVerification = (cardNumber: string | number) => {
  const strippedCardNumber = `${cardNumber}`.trim();
  if (!/^\d+$/.test(strippedCardNumber)) {
    return false; // card number should only contain digits
  }

  let sum = 0;
  let doubleNext = false;
  for (let i = strippedCardNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(strippedCardNumber.charAt(i), 10);

    if (doubleNext) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    doubleNext = !doubleNext;
  }

  return sum % 10 === 0;
};

export const hasCreditCardInfo = (value: string): boolean => {
  if (window.lsCloud.Modules.hasModule('data.pan_detection.warn')) {
    return hasCreditCardInfoV3(value);
  }

  // Remove whitespace and special characters
  const processedString = value.trim().replace(SPECIAL_CHARACTERS, '');

  return CC_ISSUER_PATTERNS.some((issuer) => {
    const allMatches = new Set(issuer.regex.exec(processedString));
    const hasMatch = allMatches.size > 0;

    const isLuhnVerified = [...allMatches]
      .filter(Boolean)
      .some((matchedTerm) => luhnVerification(matchedTerm));

    return hasMatch && isLuhnVerified;
  });
};

/**
 * Reserved SystemId starting with
 *
 * 21 - Items
 * 22 - Sales/Quotes
 * 24 - Gift Cards
 * 25 - Work Orders
 */
const SALE_OR_QUOTE_SYSTEM_ID = RegExp(/^22[0-9]{10}$/);

export const hasQuoteSystemId = (id: string) => !!SALE_OR_QUOTE_SYSTEM_ID.exec(id);
